import { computed, reactive, ref, toRefs } from 'vue';
import axios, { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { Claim, GetUserResponseDto, UserDto } from '.';
import { ProfileService, GetProfileResponseDto } from '../profile';
import { ProfileInfo } from '../../models';
import { MenuPageLink } from '../../composables';
import { CustomError } from '../common';

const initialUser: UserDto = {
    isLoggedIn: false,
    name: '',
    isEmailConfirmed: false,
    isPhoneNumberConfirmed: false,
    twoFactorEnabled: false
};
const initialProfile: ProfileInfo = { firstName: '', lastName: '', email: '', displayName: '', profileExists: false };
const initialClaims: Claim[] = [];

const useUserStore = defineStore('userStore', () => {
    const profileService = new ProfileService();

    const state = reactive({
        user: initialUser,
        claims: initialClaims,
        profile: initialProfile
    });

    const isLoading = ref(false);
    const isRequesting = ref(false);
    const isVerifying = ref(false);
    const isLoggedIn = computed(() => state.user.isLoggedIn);
    const isPhoneNumberConfirmed = computed(() => state.user.isPhoneNumberConfirmed);
    const getProfile = computed(() => state.profile);

    function getClaim(name: string) {
        return Array.isArray(state.claims) && state.claims.length ? state.claims.find(x => x.type == name)!.value : undefined;
    }

    async function loadUserSilent() {
        const { data, status } = await axios.get<Claim[]>('/bff/user', {
            headers: {
                'X-CSRF': '1'
            },
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status == 401; // allow 401 - silent-login will handle it
            }
        });

        if (status != 200) {
            return;
        }

        state.claims = data;
        state.user = {
            isLoggedIn: true,
            name: getClaim('name') || '',
            isEmailConfirmed: false,
            isPhoneNumberConfirmed: false,
            twoFactorEnabled: false
        };

        state.profile.displayName = state.user.name;
    }

    async function loadProfile(profilePage?: Record<string, MenuPageLink>, minSideLink?: Record<string, MenuPageLink>) {
        if (!state.user.isLoggedIn) return;

        const { data, status } = await axios.get<GetProfileResponseDto>('/api/bff/profile', {
            headers: {
                'X-CSRF': '1'
            },
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status == 404; // allow 404 - profile not created
            }
        });

        if (status != 200) {
            return;
        }

        state.profile.profileExists = true;
        state.profile.firstName = data.firstName;
        state.profile.lastName = data.lastName;
        state.profile.email = data.email;
        state.profile.displayName = `${data.firstName} ${data.lastName}`;
        state.profile.workEmail = data.workEmail;

        if (profilePage) {
            state.profile.profilePage = profilePage;
        }
        if (minSideLink) {
            state.profile.myPage = minSideLink;
        }
    }

    async function loadUser() {
        if (!state.user.isLoggedIn) return;

        const { data, status } = await axios.get<GetUserResponseDto>('/api/bff/profile/user', {
            headers: {
                'X-CSRF': '1'
            },
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status == 404; // allow 404 - profile not created
            }
        });

        if (status != 200) return;

        state.user.isEmailConfirmed = data.emailConfirmed;
        state.user.isPhoneNumberConfirmed = data.phoneNumberConfirmed;
        state.user.phone = data.phone;
        state.user.twoFactorEnabled = data.twoFactorEnabled;
    }

    async function requestForPhoneVerification(phone?: { countryCode: string; number: string }, recaptchaToken?: string) {
        isRequesting.value = true;

        try {
            // if logged in, use user phone number
            // else use provided phone number
            if (!phone && isLoggedIn.value) {
                phone = state.user.phone;
            }

            // if phone number is missing, throw error
            if (!phone?.countryCode || !phone.number) {
                throw new CustomError('ProfileError', '$vuetify.phoneVerification.missingParams');
            }

            isLoading.value = true;

            await profileService.sendPhoneConfirmation(phone, recaptchaToken);
        } catch (e) {
            console.error(e);

            if (isAxiosError(e)) {
                if (e.response?.status === 429) {
                    throw new CustomError('ProfileError', '$vuetify.phoneVerification.tooManyRequests');
                }

                throw new CustomError(
                    'ProfileError',
                    e.response?.data || '$vuetify.phoneVerification.requestForPhoneVerificationDefaultError'
                );
            }
        } finally {
            isLoading.value = false;
            isRequesting.value = false;
        }
    }

    async function checkPhoneVerificationCode(
        code: string,
        phone?: { countryCode: string; number: string },
        recaptchaToken?: string
    ): Promise<boolean> {
        isVerifying.value = true;

        try {
            // if logged in, use user phone number
            // else use provided phone number
            if (!phone && isLoggedIn.value) {
                phone = state.user.phone;
            }

            // if phone number is missing, throw error
            if (!phone?.countryCode || !phone.number || !code) {
                throw new CustomError('ProfileError', '$vuetify.phoneVerification.missingParams');
            }

            isLoading.value = true;
            const result = await profileService.verifyPhoneConfirmation(phone, code, recaptchaToken);

            if (isLoggedIn.value) {
                state.user.isPhoneNumberConfirmed = result;
            }

            return result;
        } catch (e) {
            console.error(e);

            if (isAxiosError(e)) {
                if (e.response?.status === 429) {
                    throw new CustomError('ProfileError', '$vuetify.phoneVerification.tooManyRequests');
                }

                throw new CustomError(
                    'ProfileError',
                    e.response?.data || '$vuetify.phoneVerification.checkPhoneVerificationCodeDefaultError'
                );
            }

            return false;
        } finally {
            isLoading.value = false;
            isVerifying.value = false;
        }
    }

    return {
        ...toRefs(state),
        isLoading,
        isRequesting,
        isVerifying,
        isLoggedIn,
        getProfile,
        isPhoneNumberConfirmed,
        getClaim,
        loadUserSilent,
        loadProfile,
        loadUser,
        requestForPhoneVerification,
        checkPhoneVerificationCode
    };
});

export default useUserStore;
